import { Avatar, Box, Typography } from "@mui/material";
import { DataGrid, GridActionsCellItem } from "@mui/x-data-grid";
import DeleteIcon from "@mui/icons-material/DeleteOutlined";
import axios from "axios";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";

const apiBaseURL = process.env.REACT_APP_API_BASE_URL;
const appColor = "#59D6DF";

const UserReports = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [totalRows, setTotalRows] = useState(0);
  const [refreshTick, setRefreshTick] = useState(0);

  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10
  });

  const navigate = useNavigate();

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const headers = {
          "Content-Type": "application/json",
          "x-access-token": `${localStorage.getItem("token")}`
        };

        const res = await axios.get(`${apiBaseURL}/api/v1/admin/get-blocked-users`, {
          headers,
          params: {
            page: paginationModel.page + 1,
            limit: paginationModel.pageSize
          }
        });

        const records = res?.data?.data || [];
        const meta = res?.data?.meta || {};

        const mappedRows = records.map((record) => ({
          id: record._id,
          blockedUser: {
            id: record?.blockedUser?._id,
            fullName: record?.blockedUser?.fullName || "N/A",
            email: record?.blockedUser?.email || "",
            profileImage: record?.blockedUser?.profileImage || ""
          },
          blockedBy: {
            id: record?.blockedBy?._id,
            fullName: record?.blockedBy?.fullName || "N/A",
            email: record?.blockedBy?.email || "",
            profileImage: record?.blockedBy?.profileImage || ""
          },
          createdAt: record?.createdAt
        }));

        setRows(mappedRows);
        setTotalRows(typeof meta.total === "number" ? meta.total : mappedRows.length);
      } catch (error) {
        console.error("Error fetching reported users:", error);
        Swal.fire({
          icon: "error",
          title: "Error",
          text: "Failed to fetch reported users. Please try again."
        });
      } finally {
        setLoading(false);
      }
    })();
  }, [paginationModel.page, paginationModel.pageSize, refreshTick]);

  const handleActionClick = (row) => {
    Swal.fire({
      title: "Take action on this reported user?",
      text: `This will delete all posts of ${row.blockedUser.fullName}.`,
      showCancelButton: true,
      showDenyButton: true,
      confirmButtonText: "Delete & Block User",
      denyButtonText: "Delete Without Blocking",
      cancelButtonText: "Cancel",
      confirmButtonColor: appColor
    })
      .then(async (result) => {
        if (result.isConfirmed || result.isDenied) {
          const blockUser = result.isConfirmed;
          try {
            const headers = {
              "Content-Type": "application/json",
              "x-access-token": `${localStorage.getItem("token")}`
            };

            const res = await axios.post(
              `${apiBaseURL}/api/v1/admin/block-user`,
              {
                userId: row.blockedUser.id,
                blockUser
              },
              { headers }
            );

            if (res?.data) {
              Swal.fire(
                "Action applied",
                blockUser
                  ? "User blocked and posts deleted."
                  : "Posts deleted, user not blocked.",
                "success"
              );
              setRefreshTick((t) => t + 1);
            }
          } catch (error) {
            console.error("Error applying action:", error);
            Swal.fire(
              "Error",
              error?.response?.data?.message || "Failed to apply action.",
              "error"
            );
          }
        } else if (result.isDismissed) {
          Swal.fire("Action cancelled", "", "info");
        }
      })
      .catch((error) => {
        console.error(error);
      });
  };

  const columns = useMemo(
    () => [
      {
        field: "blockedUser",
        headerName: "Reported User",
        flex: 1.3,
        minWidth: 260,
        sortable: false,
        renderCell: (params) => (
          <Box
            display="flex"
            alignItems="center"
            gap={1.5}
            sx={{ cursor: "pointer" }}
            onClick={() => {
              if (params.row.blockedUser?.id) {
                navigate("/users", { state: { id: params.row.blockedUser.id } });
              }
            }}
          >
            <Avatar
              src={params.row.blockedUser?.profileImage || ""}
              alt={params.row.blockedUser?.fullName || "User"}
              sx={{ width: 34, height: 34 }}
            />
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
                {params.row.blockedUser?.fullName}
              </Typography>
              {params.row.blockedUser?.email && (
                <Typography variant="caption" color="text.secondary">
                  {params.row.blockedUser.email}
                </Typography>
              )}
            </Box>
          </Box>
        )
      },
      {
        field: "blockedBy",
        headerName: "Reported By",
        flex: 1.3,
        minWidth: 260,
        sortable: false,
        renderCell: (params) => (
          <Box
            display="flex"
            alignItems="center"
            gap={1.5}
            sx={{ cursor: "pointer" }}
            onClick={() => {
              if (params.row.blockedBy?.id) {
                navigate("/users", { state: { id: params.row.blockedBy.id } });
              }
            }}
          >
            <Avatar
              src={params.row.blockedBy?.profileImage || ""}
              alt={params.row.blockedBy?.fullName || "User"}
              sx={{ width: 34, height: 34 }}
            />
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
                {params.row.blockedBy?.fullName}
              </Typography>
              {params.row.blockedBy?.email && (
                <Typography variant="caption" color="text.secondary">
                  {params.row.blockedBy.email}
                </Typography>
              )}
            </Box>
          </Box>
        )
      },
      {
        field: "createdAt",
        headerName: "Reported On",
        flex: 0.9,
        minWidth: 180,
        sortable: false,
        renderCell: (params) =>
          params.row.createdAt ? new Date(params.row.createdAt).toLocaleString() : "N/A"
      },
      {
        field: "actions",
        type: "actions",
        headerName: "Actions",
        flex: 0.5,
        minWidth: 120,
        sortable: false,
        cellClassName: "actions",
        getActions: (params) => [
          <GridActionsCellItem
            icon={<DeleteIcon />}
            label="Delete"
            onClick={() => handleActionClick(params.row)}
            color="inherit"
          />
        ]
      }
    ],
    [navigate]
  );

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <Box
        sx={{
          height: 675,
          width: "100%",
          "& .actions": {
            color: "text.secondary"
          }
        }}
      >
        <DataGrid
          rows={rows}
          columns={columns}
          disableColumnResize
          disableColumnMenu
          paginationMode="server"
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          rowSelectionModel={[]}
          onRowSelectionModelChange={() => {}}
          rowCount={totalRows}
          pageSizeOptions={[10, 20, 30, 50]}
          loading={loading}
          localeText={{ noRowsLabel: "No reported users available" }}
          sx={{
            fontFamily: `"Roboto","Helvetica","Arial",sans-serif`,
            fontWeight: 400,
            "& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within": {
              outline: "none !important"
            },
            "& .MuiDataGrid-columnHeader": {
              borderRight: "1px solid rgba(224, 224, 224, 1)",
              "& .MuiDataGrid-iconButtonContainer": {
                display: "none"
              },
              "& .MuiDataGrid-menuIcon": {
                display: "none"
              }
            },
            "& .MuiDataGrid-cell": {
              borderRight: "1px solid rgba(224, 224, 224, 1)"
            },
            "& .MuiDataGrid-row": {
              backgroundColor: "#fff",
              "&:nth-of-type(odd)": {
                backgroundColor: "#f9f9f9"
              }
            },
            "& .Mui-selected": {
              backgroundColor: "inherit !important"
            }
          }}
        />
      </Box>
    </DashboardLayout>
  );
};

export default UserReports;
